// AI 노트(회의록) 공용 헬퍼·상수·스타일 — MeetingsPage에서 분리(순수 함수, 동작 동일).
// 2026-10-21 시안 F(Notion) 값으로 교체(#1867) — 키 이름(green=주 강조 등)은 호환을 위해 그대로.

export const MEETING_THEME = {
  color: {
    page: '#ffffff',
    surface: '#ffffff',
    subtle: '#fbfbfa',
    border: '#e5e5e2',
    borderSoft: '#eeeeec',
    text: '#32312e',
    textStrong: '#32312e',
    muted: '#64635f',
    faint: '#92918d',
    slate: '#64635f',
    selectedBg: '#eaeae7',
    selectedText: '#32312e',
    iconLine: '#64635f',
    circleBorder: '#e5e5e2',
    outlineBtnBorder: '#e5e5e2',
    indigo: '#e5e5e2',
    indigoDark: '#32312e',
    indigoSoft: '#eaeae7',
    indigoBorder: '#e5e5e2',
    green: '#2383e2',
    greenDark: '#1873ce',
    greenSoft: '#eaf3fc',
    greenBorder: '#d3e7f9',
    warn: '#8d6d28',
    warnSoft: '#f8e8b3',
    danger: '#d66b60',
    dangerSoft: '#f8e4e0',
  },
  radius: { sm: '4px', md: '5px', lg: '8px', pill: '999px' },
  font: { xs: '11px', sm: '12px', base: '13px', md: '14px', lg: '15px', xl: '17px' },
  space: { xs: '4px', sm: '8px', md: '12px', lg: '16px', xl: '24px' },
  shadow: { card: '0 1px 1px #eeeeec', raised: '0 4px 24px #1c191711, 0 1px 3px #1c19170b' },
};

export const STATUS = {
  ready:      { label: '대기 (시작 전)', color: '#64635f', bg: '#f1f1ef' },
  uploaded:   { label: '변환 대기',   color: '#64635f', bg: '#f1f1ef' },
  processing: { label: '변환 중…',  color: '#8d6d28', bg: '#f8e8b3' },
  done:       { label: '완료',       color: '#426b55', bg: '#dcebe2' },
  failed:     { label: '실패',       color: '#a4564d', bg: '#f8e4e0' },
  cancelling: { label: '중지 중…',   color: '#8d6d28', bg: '#f8e8b3' },   // #141
  cancelled:  { label: '중지됨',     color: '#64635f', bg: '#f1f1ef' },   // #141
  diarizing:  { label: '화자 분석 중', color: '#356c9b', bg: '#d3e7f9' },
  rediarize:  { label: '화자 분석 대기', color: '#356c9b', bg: '#d3e7f9' },
};

// 전사가 비어 있는 '완료' — 그냥 '완료' 로 보이면 "STT 가 안 됐다" 로 오인된다.
// 실제 원인은 녹음에 소리가 없던 것이라(2026-08-18 meeting 103, 52분 전체 -50dB 미만),
// 상태 자체를 다르게 보여 사용자가 재녹음을 판단할 수 있게 한다.
export const NO_SPEECH_STATUS = { label: '소리 없음', color: '#92400e', bg: '#fef3c7' };

export function meetingStatusChip(m) {
  if (m && m.no_speech) return NO_SPEECH_STATUS;
  return STATUS[m && m.status] || STATUS.uploaded;
}

export const CANCELLABLE_STT_STATUSES = ['uploaded', 'processing', 'diarizing', 'rediarize'];

// 인식 언어 목록 정본 — 새 노트 화면(NewNoteView)·녹음 바(RecordingContext REC_LANGUAGE_OPTIONS)가 함께 쓴다.
// 서버 `MEETING_LANGUAGES`(routers/meetings.py) 와 코드 집합이 같아야 한다.
export const MEETING_LANGUAGE_OPTIONS = [
  { code: 'ko', label: '한국어' },
  { code: 'en', label: '영어' },
  { code: 'ja', label: '일본어' },
  { code: 'zh', label: '중국어' },
  { code: 'auto', label: '자동 감지' },
];

export const ACTIONS = { create: '생성', delete: '삭제', restore: '복원', purge: '영구삭제', edit: '편집', summarize: '요약', share: '공유', unshare: '공유해제', move: '이동', cancel: '중지', transcribe: 'STT시작' };

export function fmtDate(s) {
  if (!s) return '';
  const d = new Date(s.endsWith('Z') ? s : s + 'Z');
  return d.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}
export function fmtDur(sec) {
  if (!sec) return '';
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// ── 목록 전용 표기 ──────────────────────────────────────────────────────────
// 🔴 fmtDate·fmtDur 는 상세·녹음 화면·관리 표가 함께 쓰므로 고치지 않는다.
//    목록 행만 클로바식(오늘/어제/요일 · N초·N분·H시간 M분)으로 보이게 별도 함수를 둔다.
const WEEKDAY_KO = ['일', '월', '화', '수', '목', '금', '토'];

export function fmtListDate(s) {
  if (!s) return '';
  const text = String(s);
  const d = new Date(text.endsWith('Z') ? text : text + 'Z');   // Z 없는 값은 UTC naive(fmtDate 와 동일)
  if (Number.isNaN(d.getTime())) return '';
  const hour = d.getHours();
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  const time = `${hour < 12 ? '오전' : '오후'} ${h12}:${String(d.getMinutes()).padStart(2, '0')}`;
  const now = new Date();
  const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(d, now)) return `오늘 ${time}`;
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (sameDay(d, yesterday)) return `어제 ${time}`;
  const md = `${d.getMonth() + 1}.${d.getDate()} ${WEEKDAY_KO[d.getDay()]}`;
  if (d.getFullYear() === now.getFullYear()) return `${md} ${time}`;
  return `${d.getFullYear()}.${md} ${time}`;
}

export function fmtListDur(sec) {
  const total = Math.floor(Number(sec) || 0);
  if (total <= 0) return '';
  if (total < 60) return `${total}초`;
  if (total < 3600) return `${Math.floor(total / 60)}분`;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  return m === 0 ? `${h}시간` : `${h}시간 ${m}분`;
}

export function fmtBytes(bytes) {
  if (!bytes) return '';
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
// 변환 예상 폴백 계수(진행률 나오기 전 초기값에만 사용; 진행률 뜨면 자가보정이 우선이라 GPU 무관해짐).
const GPU_FACTOR = 0.08;   // RTX 5080 large-v3 대략(오디오×0.08). (구 0.375는 RTX 3050 기준이라 과대추정이었음)
const DIAR_FACTOR = 0.22;  // 화자분석 예상 = 오디오×0.22 (RTX 5080 pyannote 실측 ~0.2). 진행콜백 없어 고정계수. GPU 교체 시 조정.
function diarFactor(m) {
  const observed = Number(m?.diar_factor_observed);
  return Number.isFinite(observed) && observed > 0 ? observed : DIAR_FACTOR;
}
export function fmtElapsed(sec) {
  sec = Math.max(0, Math.floor(sec));
  const m = Math.floor(sec / 60), s = sec % 60;
  return m > 0 ? `${m}분 ${s}초` : `${s}초`;
}
export function parseKST(s) { return s ? Date.parse(s.endsWith('Z') ? s : s + 'Z') : 0; }
export function procInfo(m, nowMs) {
  const start = parseKST(m.updated_at) || parseKST(m.created_at);
  const elapsed = start ? (nowMs - start) / 1000 : 0;
  let txt = `${fmtElapsed(elapsed)} 경과`;
  const p = m.progress || 0;
  if (p >= 2 && p < 99 && elapsed > 3) {
    txt += ` · 남은 약 ${fmtElapsed(Math.round(elapsed * (100 - p) / p))}`;   // 진행률 기반 자가보정(GPU 무관)
  } else if (m.duration_sec > 0) {
    txt += ` · 예상 약 ${fmtElapsed(Math.round(m.duration_sec * GPU_FACTOR))}`;
  }
  return txt;
}
// 화자분석(diarizing) 예상: 진행콜백이 없어 오디오길이×DIAR_FACTOR 고정 추정 + 경과/남은 표시.
export function diarInfo(m, nowMs) {
  const start = parseKST(m.updated_at) || parseKST(m.created_at);
  const elapsed = start ? (nowMs - start) / 1000 : 0;
  let txt = `${fmtElapsed(elapsed)} 경과`;
  if (m.duration_sec > 0) {
    const total = Math.round(m.duration_sec * diarFactor(m));
    const remain = Math.max(0, total - Math.round(elapsed));
    txt += ` · 예상 약 ${fmtElapsed(total)}` + (remain > 0 ? ` (남은 ~${fmtElapsed(remain)})` : '');
  }
  return txt;
}
export function blobDownload(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}

// ── 다운로드 파일명 ─────────────────────────────────────────────────────────
// 🔴 '마지막 점 뒤 = 확장자'로 보지 않는다 — 녹음 기본 제목 '녹음 2026. 10. 6. 오후 3:12:45' 나
//    '2026.10.06 주간회의' 의 점 뒤를 확장자로 오인해 음성 파일 확장자가 빠지고 TXT·요약 이름이 잘렸다.
//    알려진 확장자(서버 meetings.py _AUDIO_EXT·_ALLOWED_AUDIO_EXT + 화면이 붙이는 txt·docx)만 확장자로 본다.
export const AUDIO_FILE_EXT_RE = /\.(webm|m4a|mp4|mp3|wav|ogg|oga|opus|aac|amr|flac|3gp)$/i;
export const MEETING_FILE_EXT_RE = /\.(webm|m4a|mp4|mp3|wav|ogg|oga|opus|aac|amr|flac|3gp|txt|docx)$/i;

// 제목 → 파일명 바탕(확장자 없음). 끝의 알려진 확장자만 뗀다. 비면 fallback.
export function meetingFileBase(title, fallback) {
  const t = String(title || '').trim();
  if (!t) return fallback;
  const base = t.replace(MEETING_FILE_EXT_RE, '').trim();
  return base || fallback;
}

// 오디오 MIME → 확장자(';codecs=…' 가 붙어도 같다). 모르면 ''.
export function audioExtForMime(mime) {
  const x = String(mime || '').toLowerCase();
  if (x.includes('webm')) return '.webm';
  if (x.includes('mp4') || x.includes('m4a')) return '.m4a';
  if (x.includes('wav')) return '.wav';
  if (x.includes('mpeg') || x.includes('mp3')) return '.mp3';
  if (x.includes('ogg')) return '.ogg';
  if (x.includes('opus')) return '.opus';
  if (x.includes('aac')) return '.aac';
  if (x.includes('flac')) return '.flac';
  if (x.includes('amr')) return '.amr';
  if (x.includes('3gp')) return '.3gp';
  return '';
}

// 음성 파일 다운로드 이름 = 제목 + MIME 확장자. MIME 을 모르면 제목에 남은 음성 확장자만 살린다.
export function audioDownloadName(title, mime) {
  const ext = audioExtForMime(mime);
  if (ext) return meetingFileBase(title, 'meeting') + ext;
  const t = String(title || '').trim();
  if (AUDIO_FILE_EXT_RE.test(t)) return t;
  return meetingFileBase(title, 'meeting');
}

// 마크다운 렌더 직전 전처리 — 단일 개행을 hard break(줄 끝 공백 2칸)로 보존한다.
// 녹음 메모는 원래 whiteSpace:'pre-wrap' 이라 단일 개행이 그대로 보였는데 마크다운은 단일 개행을
// 합치므로, 그냥 넘기면 이미 저장된 메모의 겉모습이 바뀐다. remark-breaks 의존성을 새로 넣지 않고
// 이 순수 함수로 해결한다(문자열이 아니면 '' · \r\n·\r 는 \n 으로 통일).
// 내용이 있는 줄의 끝에만 공백 2칸을 붙이고, 빈 줄(문단 구분)과 이미 공백 2칸 이상 또는
// 백슬래시로 끝나는 줄(= 이미 hard break)은 건드리지 않는다.
// ⚠️ 한계: 펜스 코드블록(``` 로 감싼 구간) 안에서는 이 공백 2칸이 리터럴로 남는다(코드 줄 끝 공백).
//          메모에 코드블록은 드물고 보이는 피해가 그 정도라 허용한다.
export function mdHardBreaks(text) {
  if (typeof text !== 'string') return '';
  return text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => {
      if (!line.trim()) return line;                                // 빈 줄(문단 구분)은 그대로
      if (/ {2,}$/.test(line) || line.endsWith('\\')) return line;  // 이미 hard break
      return `${line}  `;
    })
    .join('\n');
}

// SPEAKER_xx → '참석자 N'(등장순서, 레거시 전사용). 커스텀 이름·None은 그대로.
export function speakerDisplayMap(transcript) {
  const map = {}; let n = 0;
  (transcript || []).forEach((s) => {
    const sp = s.speaker;
    if (sp && /^SPEAKER_\d+$/.test(sp) && !(sp in map)) { n += 1; map[sp] = `참석자 ${n}`; }
  });
  return map;
}
export const dispSpk = (sp, map) => (sp ? (map[sp] || sp) : null);

// 노트 검색어를 보내기 전 정리 — 제어문자(U+0000~U+001F·U+007F: 엑셀에서 붙여넣은 탭 등)를 공백으로 바꾸고
// 앞뒤 공백을 지운다(서버는 제어문자에 400 을 낸다). 목록 검색(MeetingsPage)·홈 검색(MeetingHome)이 함께 쓴다.
// 🔴 `/[\u0000-\u001f]/` 정규식으로 쓰지 않는다 — CRA eslint `no-control-regex` 가 경고를 낸다(NotificationCenter 선례).
export function cleanSearchText(value) {
  return Array.from(String(value || ''), (ch) => {
    const code = ch.charCodeAt(0);
    return code < 32 || code === 127 ? ' ' : ch;
  }).join('').trim();
}

export const SPEAKER_COLORS = ['#f87171', '#3b82f6', '#34d399', '#fbbf24', '#a78bfa', '#fb7185', '#22d3ee', '#f97316'];

function hashString(value) {
  return String(value || '').split('').reduce((acc, ch) => ((acc * 31) + ch.charCodeAt(0)) >>> 0, 0);
}

export function speakerNumber(sp, map) {
  const label = dispSpk(sp, map) || sp || '';
  const labelMatch = String(label).match(/참석자\s*(\d+)/);
  if (labelMatch) return Number(labelMatch[1]);
  const rawMatch = String(sp || '').match(/^SPEAKER_(\d+)$/);
  if (rawMatch) return Number(rawMatch[1]) + 1;
  return null;
}

export function speakerColor(sp, map) {
  const n = speakerNumber(sp, map);
  if (n) return SPEAKER_COLORS[(n - 1) % SPEAKER_COLORS.length];
  return SPEAKER_COLORS[hashString(dispSpk(sp, map) || sp) % SPEAKER_COLORS.length];
}

export function speakerAvatar(sp, map) {
  const label = dispSpk(sp, map) || sp || '?';
  const n = speakerNumber(sp, map);
  return { label: n ? String(n) : String(label).trim().slice(0, 1) || '?', color: speakerColor(sp, map) };
}

const neutralButtonColors = new Set([MEETING_THEME.color.indigo.toLowerCase(), MEETING_THEME.color.indigoDark.toLowerCase(), '#6b7280', '#9ca3af', MEETING_THEME.color.slate.toLowerCase()]);
const greenButtonColors = new Set([MEETING_THEME.color.green.toLowerCase(), MEETING_THEME.color.greenDark.toLowerCase(), '#16a34a', '#10b981']);
const outlineButtonColors = new Set([...neutralButtonColors, '#dc2626', '#ef4444', '#b91c1c', '#b45309']);
const normalizeButtonColor = (c) => {
  const raw = String(c || '').toLowerCase();
  if (neutralButtonColors.has(raw)) return MEETING_THEME.color.text;
  return c || MEETING_THEME.color.text;
};
export const smBtn = (c) => {
  const normalized = normalizeButtonColor(c);
  const filled = greenButtonColors.has(String(normalized).toLowerCase());
  if (!filled && !outlineButtonColors.has(String(c || '').toLowerCase())) {
    return { padding: '6px 12px', borderRadius: MEETING_THEME.radius.md, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: MEETING_THEME.font.sm, background: c, color: 'white' };
  }
  return {
    padding: '6px 12px',
    borderRadius: MEETING_THEME.radius.pill,
    border: filled ? 'none' : `1px solid ${MEETING_THEME.color.outlineBtnBorder}`,
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: MEETING_THEME.font.sm,
    background: filled ? normalized : MEETING_THEME.color.surface,
    color: filled ? 'white' : normalized,
  };
};
export const moveItemStyle = (act) => ({ textAlign: 'left', padding: '10px 12px', borderRadius: MEETING_THEME.radius.md, border: '1px solid ' + (act ? MEETING_THEME.color.indigo : MEETING_THEME.color.borderSoft), background: act ? MEETING_THEME.color.indigoSoft : MEETING_THEME.color.surface, cursor: 'pointer', fontSize: MEETING_THEME.font.md, color: act ? MEETING_THEME.color.indigoDark : '#374151', fontWeight: act ? 700 : 400 });
export const thAdmin = { padding: '8px 12px', fontWeight: 700, color: '#475569', fontSize: '12px' };
export const tdAdmin = { padding: '7px 12px', color: '#1f2937' };
export const softWrap = { overflowWrap: 'anywhere', wordBreak: 'break-word' };
